import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import Link from 'next/link';
import { useBasePath } from '../../../lib/use-base-path';
import styles from './TextButton.module.css';

// THE inline link-style button — Figma "⚠️ Symphony DS" → Text button (283:4833).
// Never hand-roll one; see CLAUDE.md → "Buttons".
//
// Distinct from `<Button variant="text">`, which is Figma's Ghost *Type*: a real
// 26–42px box with a hover fill. This one has no box at all — its height is its
// line-height. Its hover is an ink-colour shift, NOT an underline (Figma 283:4883
// has no underline in any state). Reach for it when a sentence, a card footer or a
// form needs an actionable word; reach for Button when you need a quiet button.
//
// The design's axes are narrower than Button's on purpose: two skins (no Accent),
// no Type, three sizes, and an `underline` toggle (an explicit opt-in for a link
// that must read as one inside body text — not part of the hover behaviour).
//
// Polymorphic: pass `href` and it renders a same-origin Next <Link> (with the
// active mount prefix) instead of a <button>.
export type TextButtonSize = 'tiny' | 'small' | 'medium';
export type TextButtonColor = 'standard' | 'destructive';

type CommonProps = {
  size?: TextButtonSize;
  color?: TextButtonColor;
  /** Always underlined. Off (the default) never underlines — hover is an ink shift. */
  underline?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  children?: ReactNode;
  className?: string;
};

type AsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | keyof CommonProps> & { href?: undefined };

type AsLink = CommonProps & {
  href: string;
  /** Skip the mount-prefix rewrite for external / already-absolute URLs. */
  external?: boolean;
  /** Override the Next <Link> `as` path (default: the active mount + href). */
  linkAs?: string;
  target?: string;
  rel?: string;
  onClick?: () => void;
  'aria-label'?: string;
};

export type TextButtonProps = AsButton | AsLink;

function classesFor({ size = 'medium', color = 'standard', underline, className }: CommonProps) {
  return [styles.root, styles[`size-${size}`], styles[`color-${color}`], underline && styles.underline, className]
    .filter(Boolean)
    .join(' ');
}

export const TextButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, TextButtonProps>(function TextButton(
  props,
  ref,
) {
  const basePath = useBasePath();
  const { startIcon, endIcon, children } = props;
  const inner = (
    <>
      {startIcon}
      {children}
      {endIcon}
    </>
  );

  if (props.href !== undefined) {
    const { href, external, linkAs, size, color, underline, startIcon: _s, endIcon: _e, children: _c, className, ...rest } = props;
    const cls = classesFor(props);
    if (external) {
      return (
        <a ref={ref as React.Ref<HTMLAnchorElement>} href={href} className={cls} {...rest}>
          {inner}
        </a>
      );
    }
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={href}
        as={linkAs ?? `${basePath}${href}`}
        className={cls}
        {...rest}
      >
        {inner}
      </Link>
    );
  }

  const { size, color, underline, startIcon: _s, endIcon: _e, children: _c, className, type, ...rest } = props;
  return (
    <button ref={ref as React.Ref<HTMLButtonElement>} type={type ?? 'button'} className={classesFor(props)} {...rest}>
      {inner}
    </button>
  );
});
